import {
  type DeterministicEval,
  type Eval,
  type EvalIssueType,
  type GuardrailFallbackType,
  type RemediationStatus,
  type SmeVisibleRemediationStatus,
} from '@/client/types.gen'

/**
 * Internal mapping type for the discriminated union of evaluation items.
 * Maps type keys to their corresponding value types.
 */
type EvalItemMap = {
  semantic: Eval
  deterministic: DeterministicEval
}

/**
 * Type key for discriminating between evaluation and guardrail items.
 * Can be either 'semantic' or 'deterministic'.
 */
export type EvalItemType = keyof EvalItemMap

/**
 * Constants for evaluation item types used in discriminated unions.
 *
 * Provides type-safe string constants for distinguishing between regular
 * evaluations and deterministic guardrails in the unified grid system.
 *
 * @example
 * ```tsx
 * if (evalItem.type === EVAL_ITEM_TYPES.eval) {
 *   // Handle regular evaluation
 * } else if (evalItem.type === EVAL_ITEM_TYPES.deterministicGuardrail) {
 *   // Handle deterministic guardrail
 * }
 * ```
 */
export const EVAL_ITEM_TYPES = {
  semantic: 'semantic',
  deterministic: 'deterministic',
} as const satisfies Record<string, EvalItemType>

/**
 * Discriminated union type for evaluation and guardrail items.
 *
 * Represents either a probabilistic evaluation or a deterministic guardrail
 * with proper type discrimination. This allows TypeScript to narrow types
 * based on the `type` property.
 *
 * @example
 * ```tsx
 * function handleItem(item: EvalItem) {
 *   if (item.type === 'eval') {
 *     // item.value is typed as Eval
 *     console.log(item.value.eval_key)
 *   } else {
 *     // item.value is typed as DeterministicGuardrail
 *     console.log(item.value.id)
 *   }
 * }
 * ```
 */
export type EvalItem = {
  [K in keyof EvalItemMap]: { type: K; id: string; value: EvalItemMap[K] }
}[keyof EvalItemMap]

/**
 * UI terminology configuration for evaluation and guardrail displays.
 *
 * Provides consistent naming across the UI for both evaluation and guardrail
 * contexts, including singular/plural forms and verb conjugations.
 *
 * @property itemType - Singular capitalized form (e.g., "Evaluation", "Guardrail")
 * @property itemTypePlural - Plural capitalized form (e.g., "Evaluations", "Guardrails")
 * @property itemTypeVerb - Infinitive verb form (e.g., "evaluate", "guardrail")
 * @property itemTypeVerbPast - Past tense verb form (e.g., "evaluated", "guardrailed")
 */
export interface EvalItemLabels {
  itemType: string // 'Guardrail' | 'Evaluation'
  itemTypePlural: string // 'Guardrails' | 'Evaluations'
  itemTypeVerb: string // 'guardrail' | 'evaluate'
  itemTypeVerbPast: string // 'guardrailed' | 'evaluated'
}

/**
 * Predefined label configurations for evaluation and guardrail terminology.
 *
 * Provides consistent terminology across the application for both the
 * evaluations page and the unified guardrails page.
 *
 * @example
 * ```tsx
 * <EvalsGridProvider labels={EVAL_ITEM_LABELS.eval} ...>
 *   {/* Uses "Evaluation" terminology *\/}
 * </EvalsGridProvider>
 *
 * <EvalsGridProvider labels={EVAL_ITEM_LABELS.guardrail} ...>
 *   {/* Uses "Guardrail" terminology *\/}
 * </EvalsGridProvider>
 * ```
 */
export const EVAL_ITEM_LABELS = {
  eval: {
    itemType: 'Evaluation',
    itemTypePlural: 'Evaluations',
    itemTypeVerb: 'evaluate',
    itemTypeVerbPast: 'evaluated',
  },
  guardrail: {
    itemType: 'Guardrail',
    itemTypePlural: 'Guardrails',
    itemTypeVerb: 'guardrail',
    itemTypeVerbPast: 'guardrailed',
  },
} as const satisfies Record<'eval' | 'guardrail', EvalItemLabels>

export { type EvalIssueType } from '@/client/types.gen'

/**
 * Represents an evaluation key paired with its human-readable display label.
 * Used for filter dropdowns and other UI components that need both the
 * evaluation key (for API calls) and display name (for presentation).
 *
 * @property key - The evaluation key used in API requests and internal logic
 * @property label - The human-friendly display name for UI presentation
 *
 * @example
 * ```tsx
 * const evalKeys: EvalKeyLabel[] = [
 *   { key: 'trustworthiness', label: 'Trustworthiness' },
 *   { key: 'context_sufficiency', label: 'Context Sufficiency' },
 * ]
 * ```
 */
export interface EvalKeyLabel {
  key: string
  label: string
}

// Constant for eval score prefix - matches backend .eval. prefix
export const EVAL_SCORE_PREFIX = '.eval.' as const

export const EVAL_SCORE_TYPES = [
  'trustworthiness',
  'context_sufficiency',
  'response_helpfulness',
  'query_ease',
  'response_groundedness',
] as const satisfies readonly string[]

export type EvalScoreType = (typeof EVAL_SCORE_TYPES)[number]

export const sortLogsByOptions = [
  'created_at',
  'primary_eval_issue_score',
  `${EVAL_SCORE_PREFIX}trustworthiness`,
  `${EVAL_SCORE_PREFIX}context_sufficiency`,
  `${EVAL_SCORE_PREFIX}response_helpfulness`,
  `${EVAL_SCORE_PREFIX}query_ease`,
  `${EVAL_SCORE_PREFIX}response_groundedness`,
] as const satisfies readonly string[]

export const sortLogGroupsByOptions = [
  'created_at',
  'primary_eval_issue_score',
  'total_count',
  'impact_score',
] as const satisfies readonly string[]

export type SortClusterBy = (typeof sortLogsByOptions)[number]

export const sortRemediationsByOptions = [
  'resolved_logs_count',
  'created_at',
  'last_edited_at',
] as const satisfies readonly string[]

export const remediationStatuses = [
  'ACTIVE',
  'ACTIVE_WITH_DRAFT',
  'DRAFT',
  'PAUSED',
] as const satisfies readonly SmeVisibleRemediationStatus[]

export const evalIssueTypes = [
  'difficult_query',
  'search_failure',
  'hallucination',
  'unhelpful',
  'ungrounded',
] as const satisfies readonly EvalIssueType[]

export const REMEDIATION_STATUSES = [
  'ACTIVE',
  'PAUSED',
  'DRAFT',
] as const satisfies readonly RemediationStatus[]

export const GUARDRAILED_FALLBACK_TYPES = {
  aiGuidance: 'ai_guidance',
  expertAnswer: 'expert_answer',
} as const satisfies Record<string, GuardrailFallbackType>

export const GUARDRAILED_FALLBACK_TYPES_TO_LABELS = {
  ai_guidance: 'Expert Guidance',
  expert_answer: 'Expert Answer',
} as const satisfies Record<GuardrailFallbackType, string>
